import { Box, IconChevron, Link, Text } from 'braid-design-system';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useElementWidth } from '#src/hooks/useElementWidth.ts';
import { useWindowWidth } from '#src/hooks/useWindowWidth.ts';

import { useLocation } from 'react-router';

export type Breadcrumb = {
  title: string;
  href: string;
};

type SizedPart = {
  text: string;
  href?: string;
  size: number;
};

export const Breadcrumbs = ({
  breadcrumbs,
  visibility,
}: {
  breadcrumbs: Breadcrumb[] | null;
  visibility?: unknown;
}) => {
  // Text sizing can be responsive, so record windowWidth to trigger recalculation
  const windowWidth = useWindowWidth();
  const containerRef = useRef<HTMLDivElement>(null);
  const availableWidth = useElementWidth(containerRef);
  const location = useLocation();

  const [{ withSizes, dividerSize, ellipsisSize }, setSizings] = useState<{
    withSizes: SizedPart[];
    dividerSize: number;
    ellipsisSize: number;
  }>({
    withSizes: (breadcrumbs ?? []).map((breadcrumb) => ({
      text: breadcrumb.title,
      href: breadcrumb.href,
      size: 0,
    })),
    dividerSize: 0,
    ellipsisSize: 0,
  });

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const baseSpan = element.childNodes[0] as HTMLSpanElement;
    if (!baseSpan) {
      return;
    }

    const divider = element.childNodes[1] as HTMLSpanElement;
    if (!divider) {
      return;
    }

    const computeWidth = (text: string) => {
      const span = baseSpan.cloneNode() as HTMLSpanElement;
      span.textContent = text;
      element.appendChild(span);
      const width = span.offsetWidth;
      element.removeChild(span);
      return width;
    };

    setSizings({
      withSizes: (breadcrumbs ?? []).map((breadcrumb) => ({
        text: breadcrumb.title,
        href: breadcrumb.href,
        size: computeWidth(breadcrumb.title),
      })),
      dividerSize: divider.offsetWidth,
      ellipsisSize: computeWidth('...'),
    });
  }, [breadcrumbs, visibility, windowWidth]);

  const toRender = useMemo(() => {
    // Start with the full list of items
    // Remove from after the 0th index until the total width is less than availableWidth,
    // keeping the most specific items

    const totalWidth = (
      currentItems: typeof withSizes,
      withEllipsis: boolean,
    ) => {
      const currentItemSum = currentItems.reduce(
        (acc, { size }) => acc + size,
        0,
      );
      const sumWithEllipsis = withEllipsis
        ? currentItemSum + ellipsisSize
        : currentItemSum;
      const numDividers = currentItems.length - 1 + (withEllipsis ? 1 : 0);
      const dividerSum = numDividers * dividerSize;
      return sumWithEllipsis + dividerSum;
    };

    let current = withSizes;
    let withEllipsis = false;

    while (
      current.length > 2 &&
      totalWidth(current, withEllipsis) > availableWidth
    ) {
      current = [current[0], ...current.slice(2)];
      withEllipsis = true;
    }

    if (withEllipsis) {
      current = [
        current[0],
        { text: '...', size: ellipsisSize },
        ...current.slice(1),
      ];
    }

    return current;
  }, [withSizes, availableWidth, dividerSize, ellipsisSize]);

  return (
    <Box
      ref={containerRef}
      width="full"
      display="flex"
      style={{
        whiteSpace: 'nowrap',
        visibility:
          breadcrumbs && breadcrumbs.length > 1 ? 'visible' : 'hidden',
      }}
    >
      {toRender.map((breadcrumb, index) => {
        const active = location.pathname === breadcrumb.href;

        return (
          <React.Fragment key={index}>
            {index > 0 && (
              <Text size="xsmall" tone="secondary">
                &nbsp;
                <IconChevron direction="right" />
                &nbsp;
              </Text>
            )}
            <Text size="small" tone={active ? 'neutral' : 'secondary'}>
              <Box
                component={breadcrumb.href ? Link : 'span'}
                href={breadcrumb.href}
              >
                {breadcrumb.text}
              </Box>
            </Text>
          </React.Fragment>
        );
      })}
    </Box>
  );
};
